'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Search, Menu, X } from 'lucide-react';
import { searchableItems } from '@/data/kavrisData';
import StatusBadge from '@/components/ui/StatusBadge';
import Link from 'next/link';

interface AppTopbarProps {
  onMobileMenuToggle: () => void;
  mobileMenuOpen: boolean;
}

export default function AppTopbar({ onMobileMenuToggle, mobileMenuOpen }: AppTopbarProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredResults = searchQuery.trim().length > 1
    ? searchableItems.filter(
        item =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.category.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 8)
    : [];

  const groupedResults = filteredResults.reduce<Record<string, typeof filteredResults>>((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setSearchQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setSearchQuery('');
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <header className="h-14 border-b border-border bg-card/80 backdrop-blur-sm flex items-center px-4 gap-4 relative z-30 flex-shrink-0">
      {/* Mobile menu toggle */}
      <button
        onClick={onMobileMenuToggle}
        className="lg:hidden flex items-center justify-center w-8 h-8 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        aria-label="Toggle navigation"
      >
        {mobileMenuOpen ? <X size={16} /> : <Menu size={16} />}
      </button>

      {/* Breadcrumb area — filled by page */}
      <div id="topbar-breadcrumb" className="flex-1 hidden lg:block" />

      {/* Search */}
      <div ref={searchRef} className="relative">
        <button
          onClick={() => setSearchOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all text-sm"
        >
          <Search size={13} />
          <span className="hidden sm:block text-xs">Search KAVRIS</span>
          <span className="hidden sm:flex items-center gap-0.5 ml-2 px-1 py-0.5 rounded bg-muted text-[10px] font-mono-data">
            ⌘K
          </span>
        </button>

        {searchOpen && (
          <div className="absolute right-0 top-full mt-2 w-[480px] max-w-[calc(100vw-2rem)] bg-card border border-border rounded-lg card-elevation-high overflow-hidden panel-slide-in">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
              <Search size={14} className="text-muted-foreground flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search KAVRIS..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="text-muted-foreground hover:text-foreground">
                  <X size={13} />
                </button>
              )}
            </div>

            {searchQuery.length > 1 && filteredResults.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No results found in KAVRIS for &quot;{searchQuery}&quot;
              </div>
            )}

            {filteredResults.length > 0 && (
              <div className="max-h-80 overflow-y-auto py-2">
                {Object.entries(groupedResults).map(([type, items]) => (
                  <div key={`search-group-${type}`} className="mb-2">
                    <div className="px-4 py-1 text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground">
                      {type}
                    </div>
                    {items.map(item => (
                      <Link
                        key={`search-result-${item.id}`}
                        href={item.route}
                        onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                        className="flex items-start gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">{item.title}</div>
                          <div className="text-xs text-muted-foreground truncate mt-0.5">{item.category}</div>
                        </div>
                        <StatusBadge status={item.status} size="xs" />
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {!searchQuery && (
              <div className="px-4 py-4 text-xs text-muted-foreground">
                Search across Codex, Atlas, Foundation, Product Framework and Knowledge
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}